import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { AlertCircle, CheckCircle2, XCircle, Sparkles, ChevronDown, Clock } from "lucide-react";
import { toast } from "sonner";
import JeaCard from "@/components/jea/JeaCard";
import Skeleton from "@/components/jea/Skeleton";
import EmptyState from "@/components/jea/EmptyState";
import { apiClient, formatApiError } from "@/lib/apiClient";
import { centsToUSD, relativeTime, shortDate } from "@/lib/dates";
import { cn } from "@/lib/utils";

const kindLabel = (k) => ({
  collection_verification: "Collection Event Logged",
})[k] || k;

const AlertCard = ({ alert, onAction, disabled }) => {
  const [notes, setNotes] = useState(alert.admin_notes || "");
  const [saving, setSaving] = useState(null);
  const [notesDirty, setNotesDirty] = useState(false);

  const persistNotes = async () => {
    if (!notesDirty) return;
    try {
      await apiClient.patch(`/admin/alerts/${alert.id}/notes`, { admin_notes: notes });
      setNotesDirty(false);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const doAction = async (status) => {
    setSaving(status);
    try {
      await onAction(alert.id, status, notes);
    } finally {
      setSaving(null);
    }
  };

  const match = alert.delta_cents === 0;
  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, x: 40 }}
      transition={{ duration: 0.28, ease: [0.4, 0, 0.2, 1] }}
      className="rounded-[12px] bg-bg-secondary p-6 border border-border-default border-l-[4px] border-l-warning"
      data-testid={`alert-card-${alert.id}`}
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <AlertCircle size={16} className="text-warning" />
          <h3 className="text-white font-bold text-[15px]">{kindLabel(alert.alert_type)}</h3>
        </div>
        <span className="text-text-muted text-[12px] flex items-center gap-1">
          <Clock size={11} /> {relativeTime(alert.created_at)}
        </span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-4">
        <div>
          <div className="text-white text-[14px]">
            User: <span className="text-text-secondary">{alert.user_name} <span className="text-text-muted">({alert.user_email})</span></span>
          </div>
          <div className="text-white text-[14px] mt-1">
            Debtor: <span className="text-text-secondary">{alert.debtor_name}</span>
          </div>

          <div className="mt-3 flex items-center gap-3 flex-wrap">
            <span
              className="inline-flex px-3 py-1 rounded-full text-[12px] font-semibold bg-bg-tertiary text-white border border-border-default"
              data-testid={`alert-reported-${alert.id}`}
            >
              User Reported: <span className="jea-mono ml-1">{centsToUSD(alert.user_reported_amount_cents)}</span>
            </span>
            <span
              className={cn(
                "inline-flex px-3 py-1 rounded-full text-[12px] font-bold border",
                match
                  ? "bg-accent-pink/15 text-accent-pink border-accent-pink/40"
                  : "bg-danger/15 text-danger border-danger/40",
              )}
              data-testid={`alert-verified-${alert.id}`}
            >
              Broker Verified: <span className="jea-mono ml-1">{centsToUSD(alert.broker_verified_amount_cents)}</span>
            </span>
            {match ? (
              <span className="inline-flex items-center gap-1 text-success text-[12px] font-semibold">
                <CheckCircle2 size={12} /> Match
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 text-danger text-[12px] font-semibold">
                Δ {alert.delta_cents > 0 ? "+" : ""}{centsToUSD(alert.delta_cents)}
              </span>
            )}
          </div>

          <textarea
            value={notes}
            onChange={(e) => { setNotes(e.target.value); setNotesDirty(true); }}
            onBlur={persistNotes}
            placeholder="Internal notes about this event (visible only to admins)…"
            className="mt-4 w-full min-h-[80px] rounded-[8px] bg-bg-primary border border-border-default text-white text-[13px] p-3 focus:outline-none focus:border-accent-pink focus:shadow-pink-glow"
            data-testid={`alert-notes-${alert.id}`}
          />
        </div>

        <div className="flex lg:flex-col items-stretch justify-end gap-2 lg:min-w-[180px]">
          <button
            type="button"
            disabled={disabled || saving !== null}
            onClick={() => doAction("confirmed")}
            className="h-10 px-4 rounded-[8px] bg-success text-[#091424] font-bold text-[13px] hover:bg-success/90 disabled:opacity-50 transition-colors inline-flex items-center justify-center gap-2"
            data-testid={`alert-confirm-${alert.id}`}
          >
            {saving === "confirmed" ? "Confirming…" : (<><CheckCircle2 size={14} /> Confirm Amount</>)}
          </button>
          <button
            type="button"
            disabled={disabled || saving !== null}
            onClick={() => doAction("disputed")}
            className="h-10 px-4 rounded-[8px] bg-danger text-white font-bold text-[13px] hover:bg-danger/90 disabled:opacity-50 transition-colors inline-flex items-center justify-center gap-2"
            data-testid={`alert-dispute-${alert.id}`}
          >
            {saving === "disputed" ? "Disputing…" : (<><XCircle size={14} /> Dispute Amount</>)}
          </button>
        </div>
      </div>
    </motion.div>
  );
};

const ResolvedList = ({ alerts, tone }) => {
  const [open, setOpen] = useState(false);
  if (!alerts.length) return null;
  return (
    <div className="mt-6" data-testid={`resolved-${tone}`}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between p-3 rounded-[8px] bg-bg-secondary border border-border-default text-text-secondary hover:text-white transition-colors"
      >
        <span className="jea-caption">
          {tone === "confirmed" ? "Confirmed" : "Disputed"} Recently ({alerts.length})
        </span>
        <ChevronDown size={14} className={cn("transition-transform", open && "rotate-180")} />
      </button>
      {open && (
        <div className="mt-2 space-y-2">
          {alerts.map((a) => (
            <div key={a.id} className="p-3 rounded-[8px] bg-bg-primary border border-border-default flex items-center gap-3 text-[12px]">
              <span className="text-white flex-1 truncate">{a.debtor_name} · {a.user_email}</span>
              <span className="jea-mono text-text-secondary">Reported {centsToUSD(a.user_reported_amount_cents)}</span>
              <span className={cn("jea-mono font-bold", tone === "confirmed" ? "text-success" : "text-danger")}>
                → {centsToUSD(a.broker_verified_amount_cents)}
              </span>
              <span className="jea-date text-text-muted w-[110px] text-right">{relativeTime(a.actioned_at || a.created_at)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const AdminAlertsPage = () => {
  const [alerts, setAlerts] = useState([]);
  const [confirmed, setConfirmed] = useState([]);
  const [disputed, setDisputed] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [pending, conf, disp] = await Promise.all([
        apiClient.get("/admin/alerts?status=pending"),
        apiClient.get("/admin/alerts?status=confirmed&limit=20"),
        apiClient.get("/admin/alerts?status=disputed&limit=20"),
      ]);
      setAlerts(pending.data.alerts || []);
      setConfirmed(conf.data.alerts || []);
      setDisputed(disp.data.alerts || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const onAction = async (alertId, status, admin_notes) => {
    try {
      await apiClient.patch(`/admin/alerts/${alertId}`, { status, admin_notes });
      toast.success(status === "confirmed" ? "Amount confirmed." : "Amount disputed.");
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="space-y-5" data-testid="admin-alerts-page">
      <div className="space-y-1">
        <div className="jea-caption">Human Review Queue</div>
        <h1 className="text-[32px] font-extrabold tracking-[-0.02em] text-white">Alerts</h1>
        <p className="text-text-secondary text-[14px]">
          User-logged collection events need verification against broker records.
        </p>
      </div>

      {loading ? (
        <JeaCard className="p-6 space-y-4">
          {Array.from({ length: 2 }).map((_, i) => <Skeleton key={i} className="h-40" />)}
        </JeaCard>
      ) : alerts.length === 0 ? (
        <EmptyState
          icon={CheckCircle2}
          title="Inbox zero"
          description="No pending alerts require review. Newly logged collection events will appear here after broker verification."
          testid="admin-alerts-empty"
        />
      ) : (
        <div className="space-y-4">
          <AnimatePresence>
            {alerts.map((a) => (
              <AlertCard key={a.id} alert={a} onAction={onAction} />
            ))}
          </AnimatePresence>
        </div>
      )}

      <ResolvedList alerts={confirmed} tone="confirmed" />
      <ResolvedList alerts={disputed} tone="disputed" />
    </div>
  );
};

export default AdminAlertsPage;
